import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  FileIcon,
  FileSpreadsheet,
  FileText,
  ImageIcon,
  Upload,
  X,
  RefreshCw,
  ChevronDown,
  Filter,
  PlusCircle,
  Package,
  Loader2,
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import {
  Product,
  ProductAsset,
  productService,
  AssetBundle,
  QUERY_KEYS,
} from '@/services/productService';
import axiosInstance from '@/lib/enhancedAxiosInstance';
import { useToast } from '@/components/ui/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useProductAssets } from '@/hooks/useProductAssets';
import { invalidateProductAndAssets } from '@/utils/queryInvalidation';
import { useProductDetailPageTranslations } from '@/hooks/useProductDetailPageTranslations';

// UI Components
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { DatePickerWithRange } from '@/components/ui/date-range-picker';
import { Label } from '@/components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  FileTypeIcon,
  Calendar as CalendarIcon,
  Tag as TagIcon,
  Search as SearchIcon,
  SlidersHorizontal,
  ChevronLeft,
} from 'lucide-react';

// Feature flag for the asset gallery
const ENABLE_ASSET_GALLERY = true;

// Add import for AssetCard
import { AssetCard } from '@/components/products/AssetCard';
import { useDownloadAsset } from '@/hooks/useDownloadAsset';
import { useBulkDownload } from '@/hooks/useBulkDownload';
import { BundleCard } from './BundleCard';
import { assetTypeService } from '@/services/assetTypeService';
import { BulkDownloadToolbar } from './BulkDownloadToolbar';
import { PermissionGuard } from '@/components/common/PermissionGuard';
import { logError, logDebug, logWarn } from '@/utils/errorHandler';

interface AssetTabProps {
  product: Product;
}

// Group assets by base name
interface AssetGroup {
  baseName: string;
  assets: ProductAsset[];
  expanded: boolean;
}

// Interface for tracking file uploads
interface UploadingAsset {
  id: string; // Temporary client ID
  file: File;
  progress: number;
  error: string | null;
  status: 'uploading' | 'error' | 'success';
}

export const AssetsTab: React.FC<AssetTabProps> = ({ product }) => {
  const { toast: uiToast } = useToast();
  const queryClient = useQueryClient();
  const { getAssetsLabels, getAssetsNotification } = useProductDetailPageTranslations();

  // Only proceed if feature flag is enabled
  if (!ENABLE_ASSET_GALLERY) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">{getAssetsLabels.title}</p>
      </div>
    );
  }

  // Use our hook to manage assets
  const {
    assets,
    isLoading: loading,
    isError,
    error,
    refetch,
    uploadAsset,
    isUploading,
    setPrimaryAsset,
    isSettingPrimary,
    deleteAsset,
    isDeleting,
  } = useProductAssets(product.id);

  const [selectedAssets, setSelectedAssets] = useState<Set<number>>(new Set());
  const [assetGroups, setAssetGroups] = useState<AssetGroup[]>([]);
  const [uploading, setUploading] = useState<UploadingAsset[]>([]);
  const [allSelected, setAllSelected] = useState(false);
  const [isMakingPrimary, setIsMakingPrimary] = useState<number | null>(null);

  // State for renaming assets
  const [assetToRename, setAssetToRename] = useState<ProductAsset | null>(null);
  const [newAssetName, setNewAssetName] = useState<string>('');

  // Filter state
  const [filters, setFilters] = useState({
    types: new Set<string>(),
    search: '',
    dateFrom: null as Date | null,
    dateTo: null as Date | null,
    tags: new Set<string>(), // Add tags filter
  });
  const [filterSidebarOpen, setFilterSidebarOpen] = useState(false);

  // Bundle dialog state
  const [bundleDialogOpen, setBundleDialogOpen] = useState(false);
  const [bundleName, setBundleName] = useState('');
  const [bundles, setBundles] = useState<AssetBundle[]>([]);
  const [bundlesLoading, setBundlesLoading] = useState(false);

  // Add state for the sidebar expanded status
  const [sidebarExpanded, setSidebarExpanded] = useState(false);

  // Add state for individual filter sections
  const [typeFilterOpen, setTypeFilterOpen] = useState(true);
  const [dateFilterOpen, setDateFilterOpen] = useState(true);
  const [searchFilterOpen, setSearchFilterOpen] = useState(true);
  const [tagsFilterOpen, setTagsFilterOpen] = useState(true);

  // Add a state to track if the sidebar was manually expanded (to prevent auto-collapse)
  const [manuallyExpanded, setManuallyExpanded] = useState(false);

  // Group assets whenever they change
  useEffect(() => {
    groupAssetsByBaseName();
  }, [assets]);

  // Group assets by base name
  const groupAssetsByBaseName = () => {
    if (!Array.isArray(assets)) {
      logError(
        'Assets is not an array in groupAssetsByBaseName',
        {
          component: 'AssetsTab',
          action: 'groupAssetsByBaseName',
          metadata: {
            productId: product.id,
            assetsType: typeof assets,
            assets: assets,
          },
        },
        {
          showToast: false,
          userFacing: false,
        }
      );
      return;
    }

    // Create a map of baseName -> assets
    const groups = new Map<string, ProductAsset[]>();

    // Group assets by baseName
    assets.forEach(asset => {
      // Extract baseName (filename without extension)
      const baseName = asset.name?.split('.')[0] || 'Unknown';

      if (!groups.has(baseName)) {
        groups.set(baseName, []);
      }

      const group = groups.get(baseName);
      if (group) {
        group.push(asset);
      }
    });

    // Convert map to array of groups and sort assets within each group
    // Primary first, then by date (newest first)
    const assetGroups = Array.from(groups.entries()).map(([baseName, groupAssets]) => {
      // Sort assets within group: primary images first, then by upload date
      const sortedAssets = [...groupAssets].sort((a, b) => {
        // Primary images always come first
        if (a.is_primary && !b.is_primary) return -1;
        if (!a.is_primary && b.is_primary) return 1;

        // If both are primary or both aren't, sort by upload date (newest first)
        return new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime();
      });

      return {
        baseName,
        assets: sortedAssets,
        expanded: false,
      };
    });

    // Sort groups: groups with primary assets first, then alphabetically
    assetGroups.sort((a, b) => {
      const aHasPrimary = a.assets.some(asset => asset.is_primary);
      const bHasPrimary = b.assets.some(asset => asset.is_primary);

      if (aHasPrimary && !bHasPrimary) return -1;
      if (!aHasPrimary && bHasPrimary) return 1;

      // If both have or don't have primary, sort alphabetically
      return a.baseName.localeCompare(b.baseName);
    });

    setAssetGroups(assetGroups);
  };

  // Upload file handler - now uses the hook
  const uploadFile = async (upload: UploadingAsset, productId: number) => {
    if (!productId) {
      updateUploadStatus(upload.id, 'error', 'Product ID is missing');
      return;
    }

    try {
      // Mark as uploading and set initial progress
      updateUploadStatus(upload.id, 'uploading');
      updateUploadProgress(upload.id, 0);

      // Call the upload function from our hook
      await uploadAsset({
        file: upload.file,
        onProgress: progress => updateUploadProgress(upload.id, progress),
      });

      // Mark as success
      updateUploadStatus(upload.id, 'success');

      // Remove from the uploading list after a delay
      setTimeout(() => {
        setUploading(current => current.filter(u => u.id !== upload.id));
      }, 2000);
    } catch (error) {
      logError(
        'Failed to upload asset',
        {
          component: 'AssetsTab',
          action: 'uploadFile',
          metadata: {
            productId: productId,
            fileName: upload.file.name,
            fileSize: upload.file.size,
            error: error,
          },
        },
        {
          showToast: true,
          userFacing: true,
        }
      );
      updateUploadStatus(upload.id, 'error', 'Failed to upload asset');
    }
  };

  // Update upload progress
  const updateUploadProgress = (uploadId: string, progress: number) => {
    setUploading(current =>
      current.map(upload => (upload.id === uploadId ? { ...upload, progress } : upload))
    );
  };

  // Update upload status
  const updateUploadStatus = (
    uploadId: string,
    status: 'uploading' | 'error' | 'success',
    errorMessage?: string
  ) => {
    setUploading(current =>
      current.map(upload =>
        upload.id === uploadId
          ? {
              ...upload,
              status,
              error: status === 'error' ? errorMessage || 'Unknown error' : null,
            }
          : upload
      )
    );
  };

  // Retry upload
  const retryUpload = (upload: UploadingAsset) => {
    if (product.id) {
      uploadFile(upload, product.id);
    }
  };

  // Cancel upload
  const cancelUpload = (uploadId: string) => {
    setUploading(prev => prev.filter(item => item.id !== uploadId));
  };

  // Get file type based on mime type, name and url
  const getFileType = (asset?: ProductAsset): string => {
    if (!asset) {
      // Already handled above with proper error logging
      return 'document';
    }

    // Use assetTypeService for type detection
    const detectedType = assetTypeService.detectType(asset);

    // If detection fails but we have a file extension, try to guess from that
    if (detectedType === 'unknown') {
      // Try to extract extension from URL or filename
      const url = asset.url || asset.file_url || asset.file || asset.name || '';
      if (url && typeof url === 'string') {
        const extension = url.split('.').pop()?.toLowerCase() || '';

        // Common image extensions
        if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp'].includes(extension)) {
          return 'image';
        }

        // Document extensions
        if (['pdf'].includes(extension)) {
          return 'pdf';
        }

        if (['doc', 'docx', 'txt', 'rtf'].includes(extension)) {
          return 'document';
        }

        // Spreadsheet extensions
        if (['xls', 'xlsx', 'csv'].includes(extension)) {
          return 'spreadsheet';
        }

        // Video extensions
        if (['mp4', 'avi', 'mov', 'wmv'].includes(extension)) {
          return 'video';
        }

        // Audio extensions
        if (['mp3', 'wav', 'ogg'].includes(extension)) {
          return 'audio';
        }
      }

      // Final fallback based on content_type if available
      const contentType = asset.content_type || asset.type || '';
      if (contentType) {
        if (contentType.startsWith('image/')) return 'image';
        if (contentType.startsWith('video/')) return 'video';
        if (contentType.startsWith('audio/')) return 'audio';
        if (contentType.includes('pdf')) return 'pdf';
        if (contentType.includes('document') || contentType.includes('text')) return 'document';
        if (contentType.includes('spreadsheet') || contentType.includes('csv'))
          return 'spreadsheet';
      }

      // Ultimate fallback - don't return 'unknown'
      // Already handled above with proper error logging
      return 'document'; // Default to document instead of unknown
    }

    return detectedType;
  };

  // Use assetTypeService for image detection
  const isImageAsset = (asset?: ProductAsset): boolean => {
    if (!asset) return false;
    return assetTypeService.isImageAsset(asset);
  };

  // Make asset primary - now uses the hook
  const makeAssetPrimary = async (asset: ProductAsset) => {
    if (!asset || !asset.id || isSettingPrimary) return;

    // Set local loading state
    setIsMakingPrimary(asset.id);

    try {
      // Call the setPrimaryAsset from our hook
      await setPrimaryAsset(asset.id);
    } catch (error) {
      // Already handled above with proper error logging
      uiToast({
        variant: 'destructive',
        title: 'Error',
        description: getAssetsNotification('primaryError'),
      });
    } finally {
      setIsMakingPrimary(null);
    }
  };

  // Delete asset - now uses the hook
  const deleteAssetHandler = async (assetId: number) => {
    if (!assetId || isDeleting) return;

    try {
      // Call the deleteAsset from our hook
      await deleteAsset(assetId);

      // Clear selection if needed
      if (selectedAssets.has(assetId)) {
        setSelectedAssets(current => {
          const updated = new Set(current);
          updated.delete(assetId);
          return updated;
        });
      }
    } catch (error) {
      // Already handled above with proper error logging
      uiToast({
        variant: 'destructive',
        title: 'Error',
        description: getAssetsNotification('deleteError'),
      });
    }
  };

  // Archive an asset with proper invalidation
  const archiveAsset = async (assetId: number) => {
    if (!product?.id) return;
    try {
      const asset = assets.find(a => a.id === assetId);
      if (!asset) return;

      await productService.updateAsset(product.id, assetId, {
        is_archived: true,
        content_type: asset?.content_type || asset?.type || '',
      });

      // Invalidate the assets query to refresh data
      await invalidateProductAndAssets(queryClient, product.id);

      // Refetch bundles in case any were affected
      fetchBundles();

      uiToast({
        title: 'Success',
        description: getAssetsNotification('archiveSuccess'),
      });
    } catch (err) {
      // Already handled above with proper error logging
      uiToast({
        variant: 'destructive',
        title: 'Error',
        description: getAssetsNotification('archiveError'),
      });
    }
  };

  // Rename an asset with proper invalidation
  const renameAsset = async (asset: ProductAsset, newName: string) => {
    if (!product?.id) {
      logError(
        'Product ID is missing for asset rename',
        {
          component: 'AssetsTab',
          action: 'renameAsset',
          metadata: { assetId: asset.id, assetName: asset.name },
        },
        {
          showToast: true,
          userFacing: true,
        }
      );
      uiToast({
        variant: 'destructive',
        title: 'Error',
        description: getAssetsNotification('renameError'),
      });
      return;
    }

    if (!newName.trim()) {
      logError(
        'Asset name cannot be empty',
        {
          component: 'AssetsTab',
          action: 'renameAsset',
          metadata: { productId: product.id, assetId: asset.id },
        },
        {
          showToast: true,
          userFacing: true,
        }
      );
      uiToast({
        variant: 'destructive',
        title: 'Error',
        description: 'Asset name cannot be empty',
      });
      return;
    }

    try {
      const updatedAsset = await productService.updateAsset(product.id, asset.id, {
        name: newName.trim(),
      });

      setAssetToRename(null);
      setNewAssetName('');

      // Trigger a refetch to get the latest data
      refetch();

      uiToast({
        title: 'Success',
        description: getAssetsNotification('renameSuccess'),
      });
    } catch (err) {
      // Already handled above with proper error logging
      uiToast({
        variant: 'destructive',
        title: 'Error',
        description: getAssetsNotification('renameError'),
      });
    }
  };

  // Toggle selection of an asset
  const toggleAssetSelection = (assetId: number) => {
    setSelectedAssets(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(assetId)) {
        newSelected.delete(assetId);
      } else {
        newSelected.add(assetId);
      }
      return newSelected;
    });
  };

  // Format date
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d yyyy');
    } catch (err) {
      return 'Unknown date';
    }
  };

  // Determine asset icon based on file type
  const getAssetIcon = (type: string) => {
    // Convert the type to lowercase for consistent matching
    const lowerType = type.toLowerCase();

    // Handle image type
    if (lowerType === 'image' || lowerType.startsWith('image/')) {
      return <ImageIcon className="size-5" />;
    }

    // Handle document types
    if (lowerType === 'pdf' || lowerType.includes('pdf')) {
      return <FileText className="size-5 text-red-500" />;
    }

    // Handle spreadsheet types
    if (['spreadsheet', 'excel', 'csv'].some(t => lowerType.includes(t))) {
      return <FileSpreadsheet className="size-5 text-green-500" />;
    }

    // Handle text document types
    if (['text', 'doc', 'docx', 'document'].some(t => lowerType.includes(t))) {
      return <FileText className="size-5 text-blue-500" />;
    }

    // Default file icon for other types
    return <FileIcon className="size-5 text-slate-500" />;
  };

  // Function to fetch bundles
  const fetchBundles = useCallback(async () => {
    if (!product?.id) return;

    setBundlesLoading(true);
    try {
      const bundlesList = await productService.getAssetBundles(product.id);
      setBundles(bundlesList);
    } catch (error) {
      // Already handled above with proper error logging
      uiToast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load asset bundles',
      });
    } finally {
      setBundlesLoading(false);
    }
  }, [product?.id, uiToast]);

  // Fetch bundles on mount and when product changes
  useEffect(() => {
    if (product?.id) {
      fetchBundles();
    }
  }, [product?.id, fetchBundles]);

  // Update filteredAssets useMemo to include tag filtering
  const filteredAssets = useMemo(() => {
    const result = assets.filter(asset => {
      if (!asset) return false;
      // Hide archived assets
      if (asset.is_archived) return false;
      // Type filter
      if (filters.types.size > 0) {
        // Get file type with safe fallback
        const fileType = getFileType(asset);
        const assetType = fileType ? fileType.toLowerCase() : 'unknown';

        if (!filters.types.has(assetType)) {
          return false;
        }
      }

      // Date filter
      if (filters.dateFrom && asset.uploaded_at && new Date(asset.uploaded_at) < filters.dateFrom) {
        return false;
      }
      if (filters.dateTo && asset.uploaded_at) {
        // Add one day to dateTo to include the end date (user expectation for date ranges)
        const endDate = new Date(filters.dateTo);
        endDate.setDate(endDate.getDate() + 1);
        if (new Date(asset.uploaded_at) > endDate) {
          return false;
        }
      }

      // Name search
      if (filters.search && asset.name) {
        const assetName = asset.name || '';
        if (!assetName.toLowerCase().includes(filters.search.toLowerCase())) {
          return false;
        }
      }

      // Tags filter
      if (filters.tags.size > 0) {
        // Ensure assetTags is an array
        const assetTags: string[] = Array.isArray(asset.tags) ? asset.tags : [];
        // Asset must have ALL selected tags
        const hasAllSelectedTags = Array.from(filters.tags).every(tag => assetTags.includes(tag));
        if (!hasAllSelectedTags) {
          return false;
        }
      }

      return true;
    });

    return result;
  }, [assets, filters, getFileType]);

  // Add function to collect all unique tags from assets with better type safety
  const uniqueAssetTags = useMemo(() => {
    const tagSet = new Set<string>();
    assets.forEach(asset => {
      if (asset && asset.tags && Array.isArray(asset.tags)) {
        // Only add valid string tags
        asset.tags.forEach(tag => {
          if (tag && typeof tag === 'string') {
            tagSet.add(tag);
          }
        });
      }
    });
    return Array.from(tagSet).sort();
  }, [assets]);

  // Add function to handle asset updates with React Query
  const handleAssetUpdated = (updatedAsset: ProductAsset) => {
    if (!updatedAsset || !product.id) {
      // Already handled above with proper error logging
      return;
    }

    // Update the asset in the React Query cache
    queryClient.setQueryData(
      QUERY_KEYS.PRODUCT_ASSETS(product.id),
      (oldAssets: ProductAsset[] = []) => {
        return oldAssets.map(asset =>
          asset.id === updatedAsset.id ? { ...asset, ...updatedAsset } : asset
        );
      }
    );
  };

  // Update the toggleSelectAllFiltered function
  const toggleSelectAllFiltered = () => {
    if (selectedAssets.size === filteredAssets.length) {
      // If all filtered assets are selected, deselect all filtered assets
      setSelectedAssets(prev => {
        const newSelection = new Set(prev);
        filteredAssets.forEach(asset => {
          newSelection.delete(asset.id);
        });
        return newSelection;
      });
    } else {
      // Otherwise, select all filtered assets
      setSelectedAssets(prev => {
        const newSelection = new Set(prev);
        filteredAssets.forEach(asset => {
          newSelection.add(asset.id);
        });
        return newSelection;
      });
    }
  };

  const { download: downloadAsset, isLoading: isDownloadingAsset } = useDownloadAsset();
  const { download: downloadBulkAssets, isLoading: isBulkDownloading } = useBulkDownload();

  // Replace the fetchAssets call with refetch from our hook in the async onDrop handler
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!product.id) {
        uiToast({
          variant: 'destructive',
          title: 'Error',
          description: 'Product ID is missing, cannot upload assets',
        });
        return;
      }

      // Process each dropped file
      acceptedFiles.forEach(file => {
        const uploadId = `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Add file to uploading list
        setUploading(prev => [
          ...prev,
          {
            id: uploadId,
            file,
            progress: 0,
            error: null,
            status: 'uploading',
          },
        ]);

        // Start upload
        setTimeout(() => {
          uploadFile(
            {
              id: uploadId,
              file,
              progress: 0,
              error: null,
              status: 'uploading',
            },
            product.id as number
          );
        }, 100);
      });
    },
    [product.id, uiToast]
  );

  // Dropzone setup with the defined onDrop callback
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpeg', '.jpg', '.gif', '.webp'],
      'application/pdf': ['.pdf'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
      'application/zip': ['.zip'],
    },
  });

  // Update the mouseEnter handler
  const handleSidebarMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    if (!sidebarExpanded) {
      setSidebarExpanded(true);
      setManuallyExpanded(false); // Not manually expanded if triggered by hover
    }
  };

  // Update the mouseLeave handler
  const handleSidebarMouseLeave = (e: React.MouseEvent<HTMLElement>) => {
    // Only auto-collapse on desktop AND if not manually expanded
    const isDesktop = window.innerWidth >= 768;
    if (isDesktop && !manuallyExpanded) {
      setSidebarExpanded(false);
    }
  };

  // Update button onClick handlers
  const handleManualExpand = () => {
    setSidebarExpanded(true);
    setManuallyExpanded(true); // Track that user manually expanded
  };

  const handleManualCollapse = () => {
    setSidebarExpanded(false);
    setManuallyExpanded(false);
    setFilterSidebarOpen(false);
  };

  return (
    <div className="flex h-full flex-col gap-4 md:flex-row">
      {/* Sidebar for filters with mouse enter/leave handlers */}
      <aside
        className={`
          w-full overflow-hidden rounded-md border bg-card
          transition-all duration-300 ease-in-out
          ${sidebarExpanded ? 'md:w-64' : 'md:w-12'}
        `}
        onMouseEnter={handleSidebarMouseEnter}
        onMouseLeave={handleSidebarMouseLeave}
      >
        {/* Collapsed state - minimal sidebar */}
        <div
          className={`${!sidebarExpanded ? 'flex' : 'hidden'} h-full flex-col items-center justify-start pt-4`}
        >
          <Button
            variant="ghost"
            size="icon"
            onClick={handleManualExpand}
            className="size-8 rounded-full"
          >
            <Filter className="size-4" />
            <span className="sr-only">{getAssetsLabels.filters.expandFilters}</span>
          </Button>
        </div>

        {/* Expanded state - more compact header */}
        <div className={`${sidebarExpanded ? 'block' : 'hidden'} h-full overflow-hidden`}>
          <div className="flex items-center justify-between border-b p-3">
            <div className="flex items-center gap-1">
              <SlidersHorizontal className="size-4 text-muted-foreground" />
              <h3 className="text-sm font-medium">{getAssetsLabels.filters.title}</h3>
            </div>
            <div className="flex items-center gap-1">
              {(filters.types.size > 0 || filters.search || filters.dateFrom || filters.dateTo) && (
                <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                  {filters.types.size +
                    (filters.search ? 1 : 0) +
                    (filters.dateFrom || filters.dateTo ? 1 : 0)}
                </Badge>
              )}
              <Button variant="ghost" size="icon" onClick={handleManualCollapse} className="size-7">
                <ChevronLeft className="size-4" />
                <span className="sr-only">{getAssetsLabels.filters.collapseFilters}</span>
              </Button>
            </div>
          </div>

          {/* More compact scroll area and content */}
          <ScrollArea className="h-[calc(100vh-160px)]">
            <div className="space-y-4 p-4">
              {/* File Types Filter - more compact */}
              <Collapsible
                open={typeFilterOpen}
                onOpenChange={setTypeFilterOpen}
                className="space-y-2"
              >
                <CollapsibleTrigger className="flex w-full items-center justify-between text-sm font-medium transition-colors hover:text-primary">
                  <div className="flex items-center gap-2">
                    <FileTypeIcon className="size-4 text-muted-foreground" />
                    <span className="text-sm">{getAssetsLabels.filters.type}</span>
                    {filters.types.size > 0 && (
                      <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                        {filters.types.size}
                      </Badge>
                    )}
                  </div>
                  <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-1.5 pt-2">
                  {[
                    { key: 'image', label: getAssetsLabels.fileTypes.image },
                    { key: 'pdf', label: getAssetsLabels.fileTypes.pdf },
                    {
                      key: 'spreadsheet',
                      label: getAssetsLabels.fileTypes.spreadsheet,
                    },
                    {
                      key: 'document',
                      label: getAssetsLabels.fileTypes.document,
                    },
                    { key: 'other', label: getAssetsLabels.fileTypes.other },
                  ].map(fileType => (
                    <div
                      key={fileType.key}
                      className="group flex items-center rounded-md p-1 pl-4 hover:bg-muted/50"
                    >
                      <Checkbox
                        id={`filetype-${fileType.key}`}
                        checked={filters.types.has(fileType.key)}
                        onCheckedChange={checked => {
                          setFilters(f => {
                            const newTypes = new Set(f.types);
                            if (checked) {
                              newTypes.add(fileType.key);
                            } else {
                              newTypes.delete(fileType.key);
                            }
                            return { ...f, types: newTypes };
                          });
                        }}
                        aria-label={`Filter by ${fileType.label} files`}
                        className="size-3.5 focus:ring-1 focus:ring-primary/20"
                      />
                      <Label
                        htmlFor={`filetype-${fileType.key}`}
                        className="ml-2 cursor-pointer text-xs group-hover:text-foreground"
                      >
                        {fileType.label}
                      </Label>
                    </div>
                  ))}
                </CollapsibleContent>
              </Collapsible>

              <Separator className="my-1" />

              {/* Date Range Filter - more compact */}
              <Collapsible
                open={dateFilterOpen}
                onOpenChange={setDateFilterOpen}
                className="space-y-2"
              >
                <CollapsibleTrigger className="flex w-full items-center justify-between text-sm font-medium transition-colors hover:text-primary">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="size-4 text-muted-foreground" />
                    <span className="text-sm">{getAssetsLabels.filters.uploadDate}</span>
                    {(filters.dateFrom || filters.dateTo) && (
                      <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                        {getAssetsLabels.filters.activeFilter}
                      </Badge>
                    )}
                  </div>
                  <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-2 pt-2">
                  <div className="pl-4">
                    <Label className="mb-1 block text-xs">
                      {getAssetsLabels.filters.dateRange}
                    </Label>
                    <div className="inline-block">
                      <DatePickerWithRange
                        className="w-full max-w-[240px]"
                        date={
                          filters.dateFrom && filters.dateTo
                            ? { from: filters.dateFrom, to: filters.dateTo }
                            : undefined
                        }
                        setDate={dateRange => {
                          setFilters(f => ({
                            ...f,
                            dateFrom: dateRange?.from || null,
                            dateTo: dateRange?.to || null,
                          }));
                        }}
                      />
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>

              <Separator className="my-1" />

              {/* Search Filter - more compact */}
              <Collapsible
                open={searchFilterOpen}
                onOpenChange={setSearchFilterOpen}
                className="space-y-2"
              >
                <CollapsibleTrigger className="flex w-full items-center justify-between text-sm font-medium transition-colors hover:text-primary">
                  <div className="flex items-center gap-2">
                    <SearchIcon className="size-4 text-muted-foreground" />
                    <span className="text-sm">{getAssetsLabels.filters.search}</span>
                    {filters.search && (
                      <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                        {getAssetsLabels.filters.activeFilter}
                      </Badge>
                    )}
                  </div>
                  <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2">
                  <div className="pl-4">
                    <div className="space-y-1">
                      <Label htmlFor="asset-search" className="text-xs">
                        {getAssetsLabels.filters.nameSearch}
                      </Label>
                      <div className="relative">
                        <SearchIcon className="absolute left-2 top-1/2 size-3 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          id="asset-search"
                          value={filters.search}
                          onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                          placeholder={getAssetsLabels.filters.searchPlaceholder}
                          className="h-8 max-w-[180px] py-1 pl-7 text-xs"
                          aria-label="Search assets by name"
                        />
                      </div>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>

              <Separator className="my-1" />

              {/* Tags Filter */}
              <Collapsible
                open={tagsFilterOpen}
                onOpenChange={setTagsFilterOpen}
                className="space-y-2"
              >
                <CollapsibleTrigger className="flex w-full items-center justify-between text-sm font-medium transition-colors hover:text-primary">
                  <div className="flex items-center gap-2">
                    <TagIcon className="size-4 text-muted-foreground" />
                    <span className="text-sm">{getAssetsLabels.filters.tags}</span>
                    {filters.tags.size > 0 && (
                      <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                        {filters.tags.size}
                      </Badge>
                    )}
                  </div>
                  <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-1.5 pt-2">
                  {uniqueAssetTags.length > 0 ? (
                    uniqueAssetTags.map(tag => (
                      <div
                        key={tag}
                        className="group flex items-center rounded-md p-1 pl-4 hover:bg-muted/50"
                      >
                        <Checkbox
                          id={`tag-${tag}`}
                          checked={filters.tags.has(tag)}
                          onCheckedChange={checked => {
                            setFilters(f => {
                              const newTags = new Set(f.tags);
                              if (checked) {
                                newTags.add(tag);
                              } else {
                                newTags.delete(tag);
                              }
                              return { ...f, tags: newTags };
                            });
                          }}
                          aria-label={`Filter by ${tag} tag`}
                          className="size-3.5 focus:ring-1 focus:ring-primary/20"
                        />
                        <Label
                          htmlFor={`tag-${tag}`}
                          className="ml-2 cursor-pointer text-xs group-hover:text-foreground"
                        >
                          {tag}
                        </Label>
                      </div>
                    ))
                  ) : (
                    <p className="pl-4 text-xs text-muted-foreground">
                      {getAssetsLabels.filters.noTagsFound}
                    </p>
                  )}
                </CollapsibleContent>
              </Collapsible>

              <Separator className="my-1" />

              {/* Clear Filters Button - updated to include tags */}
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setFilters({
                    types: new Set(),
                    search: '',
                    dateFrom: null,
                    dateTo: null,
                    tags: new Set(),
                  })
                }
                className="h-7 w-full text-xs"
                disabled={
                  !filters.types.size &&
                  !filters.search &&
                  !filters.dateFrom &&
                  !filters.dateTo &&
                  !filters.tags.size
                }
              >
                {getAssetsLabels.filters.reset}
              </Button>
            </div>
          </ScrollArea>
        </div>
      </aside>

      {/* Main content area - more compact */}
      <main className="flex-1 p-4">
        {/* Header with title - more compact */}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold">{getAssetsLabels.title}</h2>

          {/* Mobile filter toggle */}
          <div className="flex items-center gap-2 md:hidden">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSidebarExpanded(!sidebarExpanded)}
              className="flex h-8 items-center gap-2"
            >
              <Filter className="size-3" />
              {getAssetsLabels.filters.title}
              {(filters.types.size > 0 || filters.search || filters.dateFrom || filters.dateTo) && (
                <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-xs">
                  {filters.types.size +
                    (filters.search ? 1 : 0) +
                    (filters.dateFrom || filters.dateTo ? 1 : 0)}
                </Badge>
              )}
            </Button>
          </div>
        </div>

        {/* Bulk actions toolbar - more compact */}
        <div className="mb-4 flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
          <div className="flex items-center">
            <Checkbox
              id="select-all"
              checked={selectedAssets.size > 0 && selectedAssets.size === filteredAssets.length}
              onCheckedChange={toggleSelectAllFiltered}
              className="mr-2 size-3.5"
            />
            <label htmlFor="select-all" className="text-xs font-medium">
              {getAssetsLabels.bulkActions.selectAll}
            </label>
            <span className="ml-2 text-xs text-muted-foreground">
              {getAssetsLabels.bulkActions.selected
                .replace('{{count}}', selectedAssets.size.toString())
                .replace('{{total}}', filteredAssets.length.toString())}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <PermissionGuard permission="product.change">
              <BulkDownloadToolbar
                productId={product?.id || 0}
                selectedIds={Array.from(selectedAssets)}
                onDownload={() => {
                  if (product?.id) {
                    downloadBulkAssets(product.id, Array.from(selectedAssets));
                  }
                }}
                disabled={selectedAssets.size === 0 || isBulkDownloading}
              />
            </PermissionGuard>
            <PermissionGuard permission="product.change">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setBundleDialogOpen(true)}
                disabled={selectedAssets.size === 0}
                className="h-7 text-xs"
              >
                <PlusCircle className="mr-1 size-3" />
                {getAssetsLabels.bulkActions.createBundle}
              </Button>
            </PermissionGuard>
          </div>
        </div>

        {/* Dropzone area - more compact */}
        <PermissionGuard permission="product.change">
          <div
            {...getRootProps()}
            className={cn(
              'border-2 border-dashed rounded-lg p-6 text-center transition-colors mb-4',
              isDragActive
                ? 'border-primary bg-primary/5'
                : 'border-muted-foreground/25 hover:border-primary/50'
            )}
          >
            <input {...getInputProps()} />
            <Upload className="mx-auto mb-3 size-8 text-muted-foreground" />
            <h3 className="mb-1 text-base font-medium">{getAssetsLabels.upload.dragDrop}</h3>
            <p className="mb-2 text-xs text-muted-foreground">
              {getAssetsLabels.upload.description}
            </p>
            <Button variant="outline" size="sm" type="button" className="h-7 text-xs">
              {getAssetsLabels.upload.selectFiles}
            </Button>
          </div>
        </PermissionGuard>

        {/* Upload progress indicators */}
        {uploading.length > 0 && (
          <div className="mb-10 mt-8 space-y-4">
            <h3 className="text-sm font-medium">{getAssetsLabels.upload.uploading}</h3>

            {uploading.map(upload => (
              <div key={upload.id} className="flex items-center gap-3 rounded-md border p-3">
                <div className="flex size-12 shrink-0 items-center justify-center rounded bg-muted">
                  {getAssetIcon(upload.file.type.split('/')[0])}
                </div>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{upload.file.name}</p>

                  {upload.status === 'uploading' && (
                    <>
                      <Progress value={upload.progress} className="my-1 h-2" />
                      <p className="text-xs text-muted-foreground">
                        {getAssetsLabels.upload.uploadProgress.replace(
                          '{{progress}}',
                          upload.progress.toString()
                        )}
                      </p>
                    </>
                  )}

                  {upload.status === 'error' && (
                    <p className="text-xs text-red-500">
                      {upload.error || getAssetsLabels.upload.uploadFailed}
                    </p>
                  )}

                  {upload.status === 'success' && (
                    <p className="text-xs text-green-500">
                      {getAssetsLabels.upload.uploadComplete}
                    </p>
                  )}
                </div>

                {upload.status === 'error' && (
                  <Button variant="ghost" size="icon" onClick={() => retryUpload(upload)}>
                    <RefreshCw className="size-4" />
                    <span className="sr-only">{getAssetsLabels.upload.retry}</span>
                  </Button>
                )}

                <Button variant="ghost" size="icon" onClick={() => cancelUpload(upload.id)}>
                  <X className="size-4" />
                  <span className="sr-only">{getAssetsLabels.upload.cancel}</span>
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Asset grid - smaller cards and spacing */}
        {filteredAssets.length === 0 && !uploading.length ? (
          <div className="mt-4 rounded-lg bg-muted/40 p-6 text-center">
            <FileIcon className="mx-auto mb-3 size-8 text-muted-foreground" />
            <h3 className="mb-1 text-base font-medium">
              {getAssetsLabels.emptyStates.noSearchResults}
            </h3>
            <p className="text-xs text-muted-foreground">
              {getAssetsLabels.emptyStates.noSearchResultsDescription}
            </p>
          </div>
        ) : (
          <div className="mt-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {filteredAssets
                .sort((a, b) => {
                  if (a.is_primary && !b.is_primary) return -1;
                  if (!a.is_primary && b.is_primary) return 1;
                  return new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime();
                })
                .map(asset => (
                  <AssetCard
                    key={asset.id}
                    asset={asset}
                    productId={product?.id || 0}
                    onAssetUpdated={handleAssetUpdated}
                    onMakePrimary={makeAssetPrimary}
                    onDelete={deleteAssetHandler}
                    onDownload={asset => {
                      if (!isDownloadingAsset && product?.id) {
                        downloadAsset(product.id, asset.id);
                      }
                    }}
                    isImageAsset={isImageAsset}
                    getAssetIcon={getAssetIcon}
                    getFileType={getFileType}
                    onRename={asset => {
                      setAssetToRename(asset);
                      setNewAssetName(asset.name || '');
                    }}
                    isSelected={selectedAssets.has(asset.id)}
                    onSelect={toggleAssetSelection}
                  />
                ))}
            </div>
          </div>
        )}

        {/* Bundles Section */}
        <div className="mt-16 border-t pt-10">
          <h2 className="mb-8 text-xl font-semibold">{getAssetsLabels.bundle.createTitle}</h2>

          {/* Rename Asset Dialog */}
          <Dialog
            open={!!assetToRename}
            onOpenChange={open => {
              if (!open) {
                setAssetToRename(null);
                setNewAssetName('');
              }
            }}
          >
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{getAssetsLabels.renameDialog.title}</DialogTitle>
                <DialogDescription>{getAssetsLabels.renameDialog.description}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="asset-name">{getAssetsLabels.renameDialog.nameLabel}</Label>
                  <Input
                    id="asset-name"
                    value={newAssetName}
                    onChange={e => setNewAssetName(e.target.value)}
                    placeholder={getAssetsLabels.renameDialog.nameLabel}
                    className="w-full"
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter' && newAssetName.trim() && assetToRename) {
                        renameAsset(assetToRename, newAssetName);
                      }
                    }}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setAssetToRename(null);
                    setNewAssetName('');
                  }}
                >
                  {getAssetsLabels.renameDialog.buttons.cancel}
                </Button>
                <Button
                  onClick={() => assetToRename && renameAsset(assetToRename, newAssetName)}
                  disabled={
                    !newAssetName.trim() || !!(assetToRename && newAssetName === assetToRename.name)
                  }
                >
                  {getAssetsLabels.renameDialog.buttons.save}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Bundle Dialog */}
          <Dialog open={bundleDialogOpen} onOpenChange={setBundleDialogOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{getAssetsLabels.bundle.createTitle}</DialogTitle>
                <DialogDescription>{getAssetsLabels.bundle.createDescription}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="bundle-name">{getAssetsLabels.bundle.nameLabel}</Label>
                  <Input
                    id="bundle-name"
                    value={bundleName}
                    onChange={e => setBundleName(e.target.value)}
                    placeholder={getAssetsLabels.bundle.namePlaceholder}
                  />
                </div>

                <div>
                  <h4 className="mb-2 text-sm font-medium">
                    {getAssetsLabels.bundle.selectedAssets.replace(
                      '{{count}}',
                      selectedAssets.size.toString()
                    )}
                  </h4>
                  {selectedAssets.size === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {getAssetsLabels.bundle.noAssetsSelected}
                    </p>
                  ) : (
                    <div className="max-h-40 overflow-y-auto rounded-md border p-2">
                      <ul className="space-y-1">
                        {Array.from(selectedAssets).map(assetId => {
                          const asset = assets.find(a => a.id === assetId);
                          return asset ? (
                            <li key={asset.id} className="flex items-center text-sm">
                              <span className="mr-2 size-4 shrink-0">
                                {getAssetIcon(asset.type)}
                              </span>
                              <span className="truncate">{asset.name}</span>
                            </li>
                          ) : null;
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setBundleDialogOpen(false)}>
                  {getAssetsLabels.bundle.buttons.cancel}
                </Button>
                <Button
                  variant="primary"
                  onClick={async () => {
                    if (!bundleName.trim()) {
                      alert(getAssetsNotification('bundleNameRequired'));
                      return;
                    }

                    if (selectedAssets.size === 0) {
                      alert(getAssetsNotification('bundleAssetsRequired'));
                      return;
                    }

                    try {
                      if (!product?.id) {
                        alert(getAssetsNotification('bundleMissingProductId'));
                        return;
                      }

                      const newBundle = await productService.createAssetBundle(
                        product.id as number, // Safe assertion since we checked it's not undefined above
                        bundleName.trim(),
                        Array.from(selectedAssets)
                      );

                      // Refresh bundles
                      fetchBundles();

                      // Reset and close dialog
                      setBundleName('');
                      setBundleDialogOpen(false);

                      // Clear selection
                      setSelectedAssets(new Set());

                      uiToast({
                        title: 'Success',
                        description: getAssetsLabels.notifications.bundleCreated,
                      });
                    } catch (error) {
                      uiToast({
                        variant: 'destructive',
                        title: 'Error',
                        description: getAssetsLabels.notifications.bundleCreateFailed,
                      });
                    }
                  }}
                >
                  {getAssetsLabels.bundle.buttons.create}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Bundles list */}
          {bundlesLoading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          ) : bundles.length > 0 ? (
            <div className="space-y-4">
              {bundles.map(bundle => (
                <div key={bundle.id} className="rounded-lg border p-4">
                  <h4 className="font-medium">{bundle.name}</h4>
                  <BundleCard
                    bundle={bundle}
                    productId={product?.id || 0}
                    assets={assets}
                    onDelete={(bundleId: number) => {
                      if (window.confirm('Are you sure you want to delete this bundle?')) {
                        if (product?.id) {
                          productService
                            .deleteAssetBundle(product.id, bundleId)
                            .then(() => {
                              uiToast({
                                title: 'Success',
                                description:
                                  getAssetsNotification('bundleDeleteSuccess') ||
                                  'Bundle deleted successfully',
                              });
                              // Refresh bundles
                              if (product?.id) {
                                setBundlesLoading(true);
                                productService
                                  .getAssetBundles(product.id)
                                  .then(setBundles)
                                  .finally(() => setBundlesLoading(false));
                              }
                            })
                            .catch(() => {
                              uiToast({
                                variant: 'destructive',
                                title: 'Error',
                                description: 'Failed to delete bundle',
                              });
                            });
                        }
                      }
                    }}
                    isImageAsset={isImageAsset}
                    getAssetIcon={getAssetIcon}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-lg bg-muted/40 p-12 text-center">
              <Package className="mx-auto mb-4 size-10 text-muted-foreground" />
              <h3 className="mb-1 text-lg font-medium">No bundles created yet</h3>
              <p className="mb-4 text-sm text-muted-foreground">
                Create bundles to organize and share collections of assets
              </p>
              <PermissionGuard permission="product.change">
                <Button
                  onClick={() => setBundleDialogOpen(true)}
                  disabled={selectedAssets.size === 0}
                >
                  <PlusCircle className="mr-2 size-4" />
                  {getAssetsLabels.bulkActions.createBundle}
                </Button>
              </PermissionGuard>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AssetsTab;
